import { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-toastify';
import { adminServices } from '../services/AdminServices';

const POLLING_INTERVAL = 12000; // 12 seconds (within 10-15 second range)
const DISPLAYED_NOTIFICATIONS_KEY = 'displayedNotificationIds';

export const useAdminNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const pollingIntervalRef = useRef(null);
  const displayedIdsRef = useRef(new Set());

  // Initialize displayed notification IDs from sessionStorage
  useEffect(() => {
    const stored = sessionStorage.getItem(DISPLAYED_NOTIFICATIONS_KEY);
    if (stored) {
      try {
        displayedIdsRef.current = new Set(JSON.parse(stored));
      } catch (e) {
        displayedIdsRef.current = new Set();
      }
    }
  }, []);

  // Function to get toast type based on severity
  const getToastType = (severity) => {
    switch (severity?.toUpperCase()) {
      case 'HIGH':
        return 'error';
      case 'MEDIUM':
        return 'warning';
      case 'LOW':
      default:
        return 'info';
    }
  };

  // Function to fetch notifications from API
  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminServices.storeProcedure2('adminNotifications', {});
      
      if (response.data && Array.isArray(response.data)) {
        // Sort by createdAt descending (newest first)
        const sortedNotifications = response.data.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );

        // Check for new notifications (not yet displayed)
        const newNotifications = sortedNotifications.filter(
          (notif) => !displayedIdsRef.current.has(notif.id)
        );

        // Show toast for each new notification
        newNotifications.forEach((notif) => {
          const toastType = getToastType(notif.severity);
          toast[toastType](notif.message, {
            position: 'bottom-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });

          // Mark as displayed
          displayedIdsRef.current.add(notif.id);
        });

        // Persist displayed IDs to sessionStorage
        sessionStorage.setItem(
          DISPLAYED_NOTIFICATIONS_KEY,
          JSON.stringify(Array.from(displayedIdsRef.current))
        );

        // Update notifications state
        setNotifications(sortedNotifications);

        // Update unread count
        const unread = sortedNotifications.filter((n) => !n.isRead).length;
        setUnreadCount(unread);
      }
    } catch (error) {
      console.error('Error fetching admin notifications:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Function to mark notification as read
  const markAsRead = useCallback(async (notificationId) => {
    try {
      console.log('Marking notification as read:', notificationId);
      // Call API to mark as read
      const response = await adminServices.storeProcedure2('markAsRead', { notificationId: notificationId });
      console.log('Mark as read response:', response);
      
      // Update local state immediately
      setNotifications((prev) =>
        prev.map((notif) =>
          notif.id === notificationId ? { ...notif, isRead: true } : notif
        )
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
      
      // Refresh notifications from server to ensure sync
      setTimeout(() => fetchNotifications(), 500);
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }, [fetchNotifications]);

  // Function to mark all as read
  const markAllAsRead = useCallback(async () => {
    try {
      console.log('Marking all notifications as read');
      // Call API to mark all as read
      const response = await adminServices.storeProcedure2('markAllAsRead', {});
      console.log('Mark all as read response:', response);
      
      // Update local state immediately
      setNotifications((prev) => prev.map((notif) => ({ ...notif, isRead: true })));
      setUnreadCount(0);
      
      // Refresh notifications from server to ensure sync
      setTimeout(() => fetchNotifications(), 500);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  }, [fetchNotifications]);

  // Function to clear a specific notification
  const clearNotification = useCallback((notificationId) => {
    setNotifications((prev) => prev.filter((notif) => notif.id !== notificationId));
  }, []);

  // Function to clear all notifications
  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
    displayedIdsRef.current.clear();
    sessionStorage.removeItem(DISPLAYED_NOTIFICATIONS_KEY);
  }, []);

  // Start polling on mount
  useEffect(() => {
    // Fetch immediately on mount
    fetchNotifications();

    // Set up polling interval
    pollingIntervalRef.current = setInterval(() => {
      fetchNotifications();
    }, POLLING_INTERVAL);

    // Cleanup on unmount
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [fetchNotifications]);

  return {
    notifications,
    unreadCount,
    loading,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    clearNotification,
    clearAllNotifications,
  };
};
